import { useQuery } from '@apollo/client';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getAgentModel } from 'shared/models/agent-model';
import { hasFeature, USER_FEATURE_AGENT_RUNTIME_SELECTION } from 'shared/models/user';
import { AgentChatRuntimeOptions, AgentSSE, getAgentChatStatus, stopAgentChat, streamAgentChat } from '../../api';
import { logger } from '../../common/logger';
import { formatToolDisplayName } from '../../constants/tool-display-names';
import { AgentMessage, GET_AGENT_CONVERSATION_MESSAGES } from '../../graphql/queries/agent';
import { useAgent } from '../../hooks/use-agent';
import { useLocalStorage } from '../../hooks/use-local-storage';
import { usePrevious } from '../../hooks/use-previous';
import { useSession } from '../../hooks/use-session';
import {
    AgentEffort,
    DEFAULT_AGENT_EFFORT,
    DEFAULT_AGENT_MODEL,
    normalizeAgentEffort,
    normalizeAgentModel,
    RETIRED_AGENT_MODEL_MESSAGE
} from './agent-runtime-options';
import { ChatEvent, FeedItem } from './agent-types';
import { formatDuration } from './utils';

const EVENT_PREVIEW_CHARS = 120;

function mapMessagesToFeed(messages: AgentMessage[]): FeedItem[] {
    return messages
        .map((msg) => {
            const content = msg.content;

            if (content.kind === 'user_text') {
                return { content: content.content, kind: 'user_text', ts: msg.createdAt } as FeedItem;
            }
            if (content.kind === 'assistant_text') {
                return { content: content.content, kind: 'assistant_text', ts: msg.createdAt } as FeedItem;
            }
            if (content.kind === 'compaction') {
                return {
                    event: {
                        id: msg.id,
                        kind: 'compact_boundary',
                        label: 'Context compacted',
                        showInTranscript: true,
                        ts: content.ts
                    },
                    kind: 'event',
                    ts: content.ts
                } as FeedItem;
            }
            if (content.kind === 'error') {
                return {
                    event: {
                        kind: 'error',
                        label: content.message,
                        showInTranscript: true,
                        status: content.code,
                        ts: msg.createdAt
                    },
                    kind: 'event',
                    ts: msg.createdAt
                } as FeedItem;
            }
            if (content.kind === 'chart_data') {
                return { chart: content.data, kind: 'chart', ts: msg.createdAt } as FeedItem;
            }
            if (content.kind === 'table_data') {
                return { kind: 'table', table: content.data, ts: msg.createdAt } as FeedItem;
            }
            if (content.kind === 'email_draft') {
                return {
                    draftEmailId: content.data.draftEmailId,
                    kind: 'email_draft',
                    linkType: content.data.linkType,
                    stageIndex: content.data.stageIndex,
                    ts: msg.createdAt
                } as FeedItem;
            }
            if (content.kind === 'submission_draft') {
                return {
                    draftSubmissionId: content.data.draftSubmissionId,
                    kind: 'submission_draft',
                    ts: msg.createdAt
                } as FeedItem;
            }
            if (content.kind === 'client_update_draft') {
                return {
                    draftClientUpdateId: content.data.draftClientUpdateId,
                    kind: 'client_update_draft',
                    ts: msg.createdAt
                } as FeedItem;
            }
            if (content.kind === 'job_update_draft') {
                return {
                    draftJobUpdateId: content.data.draftJobUpdateId,
                    kind: 'job_update_draft',
                    ts: msg.createdAt
                } as FeedItem;
            }
            if (content.kind === 'search_preview') {
                return {
                    kind: 'search_preview',
                    searchDraftId: content.data.searchDraftId,
                    ts: msg.createdAt
                } as FeedItem;
            }
            return null;
        })
        .filter(Boolean) as FeedItem[];
}

export interface UseConversationStateOptions {
    variant?: 'page' | 'sidebar';
    onBeforeSend?: () => void;
}

export type ConversationState = ReturnType<typeof useConversationState>;

export function useConversationState({ variant = 'page', onBeforeSend }: UseConversationStateOptions) {
    const history = useHistory();
    const location = useLocation();
    const { user } = useSession();
    const canUsePi = hasFeature(user, USER_FEATURE_AGENT_RUNTIME_SELECTION);
    const { open, onChatStarted, conversationId, onSetConversationId, prefilledMessage, onClearPrefilledMessage } =
        useAgent();
    const [input, setInput] = React.useState('');
    const [storedModel, setStoredModel] = useLocalStorage<string>('agent-model-v2', DEFAULT_AGENT_MODEL);
    const [modelSelection, setModelSelection] = React.useState<{ conversationId: string | null; model: string }>();
    const [storedEffort, setEffort] = useLocalStorage<AgentEffort>('agent-effort', DEFAULT_AGENT_EFFORT);
    const [streaming, setStreaming] = React.useState(false);
    const [assistantBuffer, setAssistantBuffer] = React.useState('');
    const bufferRef = React.useRef('');
    const hadFirstTokenRef = React.useRef(false);
    // Track when a fork is in progress (conversation ID changed via SSE, not user navigation)
    const forkInProgressRef = React.useRef(false);
    const [feed, setFeed] = React.useState<FeedItem[]>([]);
    const [error, setError] = React.useState<string | undefined>();
    const [offline, setOffline] = React.useState(!navigator.onLine);
    const [snackOpen, setSnackOpen] = React.useState(false);
    const abortRef = React.useRef<AbortController | null>(null);
    const toolRunsRef = React.useRef<Record<string, { start: number; label?: string }>>({});
    const [isThinking, setIsThinking] = React.useState(false);
    const [activeTools, setActiveTools] = React.useState<Array<{ id: string; label: string }>>([]);
    const [recovering, setRecovering] = React.useState(false);
    const recoveryTimerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
    const streamDoneRef = React.useRef(false);
    const streamStartedRef = React.useRef(false);
    const effectiveConversationIdRef = React.useRef(conversationId);
    const prevConversationId = usePrevious(conversationId);

    React.useEffect(() => {
        effectiveConversationIdRef.current = conversationId;
    }, [conversationId]);

    // Derived state
    const isTyping = isThinking && !hadFirstTokenRef.current;

    // Fetch conversation history (includes forked source messages via computed field)
    const {
        data: historyData,
        loading: historyLoading,
        refetch: refetchHistory
    } = useQuery<{
        agent_conversations_by_pk: { id: string; model: string | null; messages: AgentMessage[] } | null;
    }>(GET_AGENT_CONVERSATION_MESSAGES, {
        fetchPolicy: 'network-only',
        skip: !conversationId,
        variables: { conversationId }
    });

    const selectedModel = modelSelection?.conversationId === conversationId ? modelSelection?.model : undefined;
    const defaultModel = normalizeAgentModel(storedModel, canUsePi);
    const savedModel =
        historyData?.agent_conversations_by_pk?.id === conversationId
            ? historyData.agent_conversations_by_pk.model
            : undefined;
    const model =
        selectedModel ??
        (conversationId ? (getAgentModel(savedModel ?? '')?.value ?? savedModel ?? defaultModel) : defaultModel);
    const modelLoading = Boolean(conversationId && !selectedModel && savedModel === undefined && historyLoading);
    const effort = normalizeAgentEffort(model, storedEffort);

    const setModel = React.useCallback(
        (value: string) => {
            const selection = normalizeAgentModel(value, canUsePi);
            if (conversationId === null) setStoredModel(selection);
            setModelSelection({ conversationId, model: selection });
        },
        [canUsePi, conversationId, setStoredModel]
    );

    const hasFeed = feed.length > 0;
    const isLoadingHistory = historyLoading;
    const showIntro = !hasFeed && assistantBuffer.length === 0 && !streaming && !isLoadingHistory && !historyData;

    // Reset conversation state (without dependencies to avoid recreation)
    const resetConversationState = React.useCallback(() => {
        setModelSelection(undefined);
        setFeed([]);
        setInput('');
        setAssistantBuffer('');
        bufferRef.current = '';
        hadFirstTokenRef.current = false;
        setStreaming(false);
        setRecovering(false);
        setError(undefined);
        setIsThinking(false);
        setActiveTools([]);
        abortRef.current?.abort();
        if (recoveryTimerRef.current) {
            clearInterval(recoveryTimerRef.current);
            recoveryTimerRef.current = null;
        }
    }, []);

    // Handle conversation ID changes - reset when switching conversations
    // forkInProgressRef distinguishes SSE-initiated ID changes (forks) from user navigation
    React.useEffect(() => {
        const isFork = forkInProgressRef.current;
        forkInProgressRef.current = false; // Always reset the flag

        // Conversation changed
        if (prevConversationId && conversationId && prevConversationId !== conversationId) {
            if (!isFork) {
                // User navigated to a different conversation - reset state
                resetConversationState();
            }
            // Fork: do nothing, keep streaming
        }

        // Conversation cleared - reset state
        if (prevConversationId && !conversationId) {
            resetConversationState();
        }
    }, [conversationId, prevConversationId, resetConversationState]);

    // Load history when history data arrives
    React.useEffect(() => {
        if (feed.length > 0) {
            return;
        }

        const allMessages = historyData?.agent_conversations_by_pk?.messages ?? [];

        if (!conversationId || allMessages.length === 0) {
            return;
        }

        setFeed(mapMessagesToFeed(allMessages));
    }, [conversationId, feed.length, historyData]);

    // Online/offline status
    React.useEffect(() => {
        const onOff = () => setOffline(!navigator.onLine);
        window.addEventListener('online', onOff);
        window.addEventListener('offline', onOff);
        return () => {
            window.removeEventListener('online', onOff);
            window.removeEventListener('offline', onOff);
        };
    }, []);

    // Cleanup abort controller and recovery timer on unmount
    React.useEffect(
        () => () => {
            abortRef.current?.abort();
            if (recoveryTimerRef.current) {
                clearInterval(recoveryTimerRef.current);
                recoveryTimerRef.current = null;
            }
        },
        []
    );

    // Notify parent when chat starts
    React.useEffect(() => {
        const hasActiveChat = hasFeed || assistantBuffer.length > 0 || streaming;
        if (hasActiveChat) {
            onChatStarted();
        }
    }, [hasFeed, assistantBuffer, streaming, onChatStarted]);

    function pushTranscriptEvent(ev: ChatEvent) {
        if (!ev.showInTranscript) return;
        setFeed((prev) => prev.concat([{ ts: ev.ts, kind: 'event', event: ev }]));
    }

    function appendSuffix(current: string, incoming: string): string {
        // Simple append - consistent behavior for all modes
        return current + incoming;
    }

    const onSseEvent = (evt: AgentSSE) => {
        if (evt.type === 'conversation_created') {
            // Fork = we already have a conversationId and this is a different one
            // In that case, mark fork in progress so the effect doesn't reset state
            if (conversationId && conversationId !== evt.conversationId) {
                forkInProgressRef.current = true;
            }
            setModelSelection((previous) =>
                previous && previous.conversationId === conversationId
                    ? { ...previous, conversationId: evt.conversationId }
                    : previous
            );
            onSetConversationId(evt.conversationId);
            effectiveConversationIdRef.current = evt.conversationId;
            streamStartedRef.current = true;
            // Update URL when on /orbit page or chat sub page (handles both new chats and forks)
            if (variant === 'page' && location.pathname.startsWith('/orbit')) {
                history.push(`/orbit/chat/${evt.conversationId}`);
            }
            return;
        }

        if (evt.type === 'start' || evt.type === 'thinking' || evt.type === 'thinking_delta') {
            streamStartedRef.current = true;
            setIsThinking(true);
            return;
        }

        if (evt.type === 'token') {
            const text = evt.text;
            setAssistantBuffer((prev) => {
                const next = appendSuffix(prev, text);
                bufferRef.current = next;
                return next;
            });
            hadFirstTokenRef.current = true;
            setIsThinking(false);
            return;
        }

        if (evt.type === 'compact_boundary') {
            pushTranscriptEvent({
                id: evt.id,
                kind: 'compact_boundary',
                label: 'Context compacted',
                showInTranscript: true,
                ts: evt.ts ?? Date.now()
            });
            return;
        }

        if (evt.type === 'assistant_final') {
            const text = evt.text;
            if (process.env.NODE_ENV !== 'production') {
                logger.debug('[agent] assistant_final message', { text });
            }
            setFeed((prev) => prev.concat([{ ts: Date.now(), kind: 'assistant_text', content: text }]));
            bufferRef.current = '';
            setAssistantBuffer('');
            setIsThinking(false);
            setActiveTools([]);
            hadFirstTokenRef.current = false;
            return;
        }

        if (evt.type === 'table_data') {
            setFeed((prev) => prev.concat([{ ts: Date.now(), kind: 'table', table: evt.data }]));
            return;
        }

        if (evt.type === 'chart_data') {
            setFeed((prev) => prev.concat([{ ts: Date.now(), kind: 'chart', chart: evt.data }]));
            return;
        }

        if (evt.type === 'email_draft') {
            setFeed((prev) =>
                prev.concat([
                    {
                        draftEmailId: evt.data.draftEmailId,
                        kind: 'email_draft',
                        linkType: evt.data.linkType,
                        stageIndex: evt.data.stageIndex,
                        ts: Date.now()
                    }
                ])
            );
            return;
        }

        if (evt.type === 'submission_draft') {
            setFeed((prev) =>
                prev.concat([
                    {
                        draftSubmissionId: evt.data.draftSubmissionId,
                        kind: 'submission_draft',
                        ts: Date.now()
                    }
                ])
            );
            return;
        }

        if (evt.type === 'client_update_draft') {
            setFeed((prev) =>
                prev.concat([
                    {
                        draftClientUpdateId: evt.data.draftClientUpdateId,
                        kind: 'client_update_draft',
                        ts: Date.now()
                    }
                ])
            );
            return;
        }

        if (evt.type === 'job_update_draft') {
            setFeed((prev) =>
                prev.concat([
                    {
                        draftJobUpdateId: evt.data.draftJobUpdateId,
                        kind: 'job_update_draft',
                        ts: Date.now()
                    }
                ])
            );
            return;
        }

        if (evt.type === 'search_preview') {
            setFeed((prev) =>
                prev.concat([
                    {
                        kind: 'search_preview',
                        searchDraftId: evt.data.searchDraftId,
                        ts: Date.now()
                    }
                ])
            );
            return;
        }

        if (evt.type === 'done') {
            streamDoneRef.current = true;
            setStreaming(false);
            setIsThinking(false);
            setActiveTools([]);
            return;
        }

        if (evt.type === 'error' || evt.type === 'aborted') {
            streamDoneRef.current = true;
            setStreaming(false);
            setIsThinking(false);
            setActiveTools([]);
            const message = evt.message ?? (evt.type === 'aborted' ? 'Aborted' : 'Stream error');
            setError(String(message));
            setSnackOpen(true);
            pushTranscriptEvent({
                kind: evt.type === 'aborted' ? 'aborted' : 'error',
                label: String(message),
                showInTranscript: true,
                ts: Date.now()
            });
            return;
        }

        if (evt.type === 'tool_start' || evt.type === 'tool_end') {
            const id = String(evt.id ?? evt.tool_use_id ?? '');
            const rawName = String(evt.name ?? evt.tool_name ?? 'tool');

            // Hide internal GraphQL execution tools from the UI to reduce noise
            if (rawName.includes('execute_graphql')) {
                return;
            }

            const friendlyName = formatToolDisplayName(rawName);
            const now = Date.now();

            if (evt.type === 'tool_start') {
                toolRunsRef.current[id] = { start: now, label: friendlyName };
                setActiveTools((prev) =>
                    prev.find((t) => t.id === id) ? prev : prev.concat([{ id, label: friendlyName }])
                );
            } else {
                const started = toolRunsRef.current[id]?.start;
                const storedLabel = toolRunsRef.current[id]?.label;
                const durationMs = typeof started === 'number' ? now - started : evt.durationMs;
                const status = evt.status ?? 'success';
                const eventDetails = typeof evt.details === 'string' ? evt.details : '';
                const inputPreview = typeof evt.inputPreview === 'string' ? evt.inputPreview : '';
                const outputPreview = typeof evt.outputPreview === 'string' ? evt.outputPreview : '';
                const ipBlock = inputPreview ? `Input:\n${inputPreview.slice(0, EVENT_PREVIEW_CHARS)}` : '';
                const opBlock = outputPreview ? `Output:\n${outputPreview.slice(0, EVENT_PREVIEW_CHARS)}` : '';
                const details = eventDetails || [ipBlock, opBlock].filter(Boolean).join('\n\n');

                pushTranscriptEvent({
                    details: details || undefined,
                    durationMs,
                    id,
                    // Tool failures are recoverable and should not appear as conversation errors.
                    kind: 'tool',
                    label: storedLabel ?? friendlyName,
                    showInTranscript: true,
                    status,
                    ts: now
                });

                delete toolRunsRef.current[id];
                setActiveTools((prev) => prev.filter((t) => t.id !== id));
            }

            return;
        }

        if (evt.type === 'result') {
            const inputTokens = evt.usage?.input_tokens ?? evt.usage?.inputTokens ?? evt.usage?.input;
            const outputTokens = evt.usage?.output_tokens ?? evt.usage?.outputTokens ?? evt.usage?.output;
            const durationMs = evt.durationMs;
            const durationLabel = typeof durationMs === 'number' ? ` • duration ${formatDuration(durationMs)}` : '';
            pushTranscriptEvent({
                kind: 'result',
                label: `Usage in=${inputTokens ?? '-'} out=${outputTokens ?? '-'}` + durationLabel,
                showInTranscript: true,
                ts: Date.now()
            });
            return;
        }
    };

    const startRecovery = React.useCallback(
        (cid: string) => {
            setStreaming(false);
            setRecovering(true);
            setIsThinking(false);
            setAssistantBuffer('');
            bufferRef.current = '';
            setActiveTools([]);

            if (recoveryTimerRef.current) {
                clearInterval(recoveryTimerRef.current);
            }

            const POLL_INTERVAL_MS = 3000;
            const MAX_POLL_DURATION_MS = 600000;
            const startedAt = Date.now();

            const poll = async () => {
                if (Date.now() - startedAt > MAX_POLL_DURATION_MS) {
                    if (recoveryTimerRef.current) {
                        clearInterval(recoveryTimerRef.current);
                        recoveryTimerRef.current = null;
                    }
                    setRecovering(false);
                    setError('Recovery timed out');
                    setSnackOpen(true);
                    return;
                }

                const status = await getAgentChatStatus(cid);

                if (status && !status.active) {
                    if (recoveryTimerRef.current) {
                        clearInterval(recoveryTimerRef.current);
                        recoveryTimerRef.current = null;
                    }
                    try {
                        const result = await refetchHistory({ conversationId: cid });
                        const messages = result?.data?.agent_conversations_by_pk?.messages ?? [];
                        if (messages.length > 0) {
                            setFeed(mapMessagesToFeed(messages));
                        }
                    } catch {
                        // Refetch failed — history will load on next navigation
                    }
                    setRecovering(false);
                }
            };

            recoveryTimerRef.current = setInterval(poll, POLL_INTERVAL_MS);
            poll();
        },
        [refetchHistory]
    );

    const sendMessage = React.useCallback(
        async (text: string) => {
            if (!text || streaming || recovering || modelLoading || !getAgentModel(model)) return;
            setFeed((prev) => prev.concat([{ ts: Date.now(), kind: 'user_text', content: text }]));
            setError(undefined);

            try {
                const ac = new AbortController();
                abortRef.current = ac;
                streamDoneRef.current = false;
                streamStartedRef.current = false;
                setStreaming(true);
                setAssistantBuffer('');
                bufferRef.current = '';
                hadFirstTokenRef.current = false;
                setIsThinking(false);
                setActiveTools([]);

                let requestModel = conversationId ? selectedModel : model;
                let turnModel = model;
                if (conversationId && !requestModel) {
                    // Missing history must not silently replace a retired model.
                    const conversation =
                        savedModel !== undefined
                            ? { model: savedModel }
                            : (await refetchHistory({ conversationId })).data.agent_conversations_by_pk;
                    if (!conversation) throw new Error('Could not load conversation model');
                    const savedOption = getAgentModel(conversation.model ?? '');
                    if (conversation.model && !savedOption) throw new Error(RETIRED_AGENT_MODEL_MESSAGE);
                    turnModel = savedOption?.value ?? defaultModel;
                    if (!savedOption) requestModel = defaultModel;
                }
                ac.signal.throwIfAborted();
                if (requestModel) {
                    const selection = { conversationId, model: requestModel };
                    setModelSelection((previous) =>
                        previous?.conversationId === conversationId ? previous : selection
                    );
                }
                const turnEffort = normalizeAgentEffort(turnModel, storedEffort);
                const hasEffortOverride = turnEffort !== DEFAULT_AGENT_EFFORT;
                const runtimeOptions: AgentChatRuntimeOptions = {
                    ...(requestModel && { model: requestModel }),
                    ...(hasEffortOverride && { thinking: { effort: turnEffort } })
                };

                await streamAgentChat(text, conversationId, onSseEvent, ac.signal, runtimeOptions);

                if (!streamDoneRef.current) {
                    const recoveryId = effectiveConversationIdRef.current;
                    if (recoveryId && streamStartedRef.current) {
                        startRecovery(recoveryId);
                        return;
                    }
                    setStreaming(false);
                    setError('Connection lost before conversation started');
                    setSnackOpen(true);
                }
            } catch (e) {
                // Don't show error for user-initiated abort
                if ((e as Error)?.name === 'AbortError') {
                    setStreaming(false);
                    return;
                }
                const recoveryId = effectiveConversationIdRef.current;
                if (recoveryId && streamStartedRef.current) {
                    startRecovery(recoveryId);
                    return;
                }
                const msg = (e as Error)?.message || 'Failed to send';
                setError(msg);
                setSnackOpen(true);
                setStreaming(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            conversationId,
            streaming,
            recovering,
            startRecovery,
            model,
            modelLoading,
            selectedModel,
            savedModel,
            defaultModel,
            storedEffort,
            refetchHistory
        ]
    );

    const handleSend = async () => {
        const text = input.trim();
        if (!text) return;
        if (onBeforeSend) onBeforeSend();
        setInput('');
        await sendMessage(text);
    };

    // Auto-send pending message when set
    React.useEffect(() => {
        if (!prefilledMessage || streaming || recovering || modelLoading) return;
        if (!getAgentModel(model)) {
            setInput(prefilledMessage);
            onClearPrefilledMessage();
            return;
        }

        // New chat: send immediately when feed is empty
        // Continuing conversation: wait for history to load first
        const isNewChat = !conversationId;
        const canSend = isNewChat ? feed.length === 0 : !historyLoading;

        if (canSend) {
            const message = prefilledMessage;
            onClearPrefilledMessage();
            sendMessage(message);
        }
    }, [
        prefilledMessage,
        streaming,
        recovering,
        model,
        modelLoading,
        feed.length,
        conversationId,
        historyLoading,
        onClearPrefilledMessage,
        sendMessage
    ]);

    const handleStop = async () => {
        // First, tell the server to abort the agent turn
        if (conversationId) {
            try {
                await stopAgentChat(conversationId);
            } catch {
                // Ignore errors - best effort stop
            }
        }
        // Then abort the local connection and update state
        abortRef.current?.abort();
        setStreaming(false);
        setRecovering(false);
        if (recoveryTimerRef.current) {
            clearInterval(recoveryTimerRef.current);
            recoveryTimerRef.current = null;
        }
    };

    const handleSnackClose = () => setSnackOpen(false);

    return {
        // State
        activeTools,
        assistantBuffer,
        effort,
        error,
        feed,
        historyLoading: isLoadingHistory,
        input,
        isThinking,
        isTyping,
        model,
        modelLoading,
        offline,
        open,
        recovering,
        showIntro,
        snackOpen,
        streaming,

        // Handlers
        handleSend,
        handleSnackClose,
        handleStop,
        setEffort,
        setInput,
        setModel
    };
}
